import React from 'react';
import PropTypes from 'prop-types';

import { getSiteKey } from 'constants/clients';
import { normalizeWhatsappNumber } from 'helpers/whatsappOrderMessage';

import './ConstructionLanding.scss';

const cleanPhoneHref = (phone) => String(phone || '').replace(/[^\d+]/g, '');

const getBrandName = (client) => (
  client?.name_show
  || client?.name
  || getSiteKey()
  || 'Tu tienda'
);

const getDomain = () => {
  if (typeof window === 'undefined') return '';
  return window.location.hostname.replace(/^www\./, '');
};

const ConstructionLanding = ({ client, whatsapp }) => {
  const brandName = getBrandName(client);
  const email = client?.email_info || client?.email || '';
  const phone = client?.phone || client?.whatsapp || whatsapp?.number || '';
  const address = client?.address || '';
  const whatsappNumber = normalizeWhatsappNumber(
    whatsapp?.number || client?.whatsapp || client?.phone,
  );
  const domain = getDomain();

  const contactItems = [
    email && {
      label: 'Email',
      value: email,
      href: `mailto:${email}`,
      icon: 'fa-envelope-o',
    },
    phone && {
      label: 'Teléfono',
      value: phone,
      href: `tel:${cleanPhoneHref(phone)}`,
      icon: 'fa-phone',
    },
    address && {
      label: 'Dirección',
      value: address,
      href: `https://maps.google.com/?q=${encodeURIComponent(address)}`,
      icon: 'fa-map-marker',
    },
  ].filter(Boolean);

  return (
    <main className="construction-landing">
      <section className="construction-hero" aria-label="Página web en construcción">
        <div className="construction-hero__content">
          <div className="construction-hero__brand">
            {client?.image ? (
              <img src={client.image} alt={brandName} className="construction-hero__logo" />
            ) : (
              <span className="construction-hero__monogram" aria-hidden="true">
                {String(brandName).charAt(0).toUpperCase()}
              </span>
            )}
            <span>{brandName}</span>
          </div>

          <p className="construction-hero__eyebrow">Muy pronto online</p>
          <h1>Página web en construcción</h1>
          <p className="construction-hero__copy">
            Estamos preparando la tienda para que puedas conocer productos,
            novedades y formas de compra.
          </p>

          <div className="construction-hero__actions">
            {whatsappNumber && (
              <a
                href={`https://wa.me/${whatsappNumber}`}
                className={[
                  'construction-hero__button',
                  'construction-hero__button--primary',
                ].join(' ')}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fa fa-whatsapp" aria-hidden="true" />
                Contactar por WhatsApp
              </a>
            )}
            {email && (
              <a href={`mailto:${email}`} className="construction-hero__button">
                <i className="fa fa-envelope-o" aria-hidden="true" />
                Enviar email
              </a>
            )}
          </div>
        </div>

        <aside className="construction-panel" aria-label="Datos de contacto">
          <div className="construction-panel__topline" />
          <h2>Contacto</h2>
          {contactItems.length ? (
            <ul className="construction-contact-list">
              {contactItems.map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    target={item.label === 'Dirección' ? '_blank' : undefined}
                    rel="noreferrer"
                  >
                    <i className={`fa ${item.icon}`} aria-hidden="true" />
                    <span>
                      <strong>{item.label}</strong>
                      {item.value}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="construction-panel__empty">
              Los datos de contacto estarán disponibles en breve.
            </p>
          )}

          <div className="construction-domain">
            <span>Sitio</span>
            <strong>{domain || brandName}</strong>
          </div>
        </aside>
      </section>
    </main>
  );
};

ConstructionLanding.defaultProps = {
  client: null,
  whatsapp: null,
};

ConstructionLanding.propTypes = {
  client: PropTypes.shape({
    address: PropTypes.string,
    email: PropTypes.string,
    email_info: PropTypes.string,
    image: PropTypes.string,
    name: PropTypes.string,
    name_show: PropTypes.string,
    phone: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    whatsapp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  whatsapp: PropTypes.shape({
    number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

export default ConstructionLanding;
